import { Link } from "react-router-dom";
import styled from "styled-components";

import { SiteNav, SiteFooter } from "../../components/fragments/ProductChrome";
import { Container } from "../../components/styles/Layout";
import { useStaticSeo } from "../../hooks/useSeo";
import { PRODUCT_SEO } from "../../data/seo";

/*
 * Who runs Doorstep Data, and how to reach them.
 *
 * WHY THERE IS NO FORM. The agency site has one; this does not need one. A
 * form here would mean collecting a name and an email address on a site
 * whose entire privacy claim is that it collects nothing - and it would be
 * a worse way to reach us than the mailto, because a form can fail silently
 * and an email cannot. (The agency's form currently sends from a misspelt
 * address, which is exactly that failure.)
 *
 * The company details are not decoration. A trading name has to identify
 * the legal entity behind it, with its registered number and a geographic
 * address, somewhere easily and permanently accessible - which is also the
 * footer's job, but a reader looking for who they are dealing with comes
 * here.
 */

const Head = styled.header`
    background: #0f4c5c;
    color: #fff;
    padding: 2.75rem 0 2.25rem;
    h1 {
        margin: 0 0 0.4rem;
        font-size: 1.9rem;
        @media (max-width: 560px) {
            font-size: 1.55rem;
        }
    }
    p {
        margin: 0;
        opacity: 0.85;
        font-size: 0.95rem;
    }
`;

const Body = styled.div`
    max-width: 46rem;
    padding: 2rem 0 1rem;
    color: #3d4a4f;
    line-height: 1.65;
    h2 {
        font-size: 1.15rem;
        color: #0f4c5c;
        margin: 2rem 0 0.5rem;
    }
    h2:first-of-type {
        margin-top: 0;
    }
    p {
        margin: 0 0 0.9rem;
    }
`;

const Cards = styled.div`
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(auto-fit, minmax(min(16rem, 100%), 1fr));
    margin: 0 0 1.75rem;
`;
const Card = styled.div`
    border: 1px solid #e0e7ea;
    border-radius: 6px;
    padding: 1rem 1.1rem;
    background: #fff;
    h3 {
        margin: 0 0 0.35rem;
        font-size: 0.78rem;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #6a7b82;
    }
    p {
        margin: 0;
        font-size: 0.95rem;
        line-height: 1.55;
    }
`;

const Details = styled.dl`
    margin: 0;
    border-top: 1px solid #e6ebed;
    dt {
        font-size: 0.78rem;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #6a7b82;
        margin: 0.9rem 0 0.15rem;
    }
    dd {
        margin: 0 0 0.9rem;
    }
`;

export default function DoorstepContact() {
    useStaticSeo(PRODUCT_SEO["/contact"]);

    return (
        <>
            <SiteNav />
            <Head>
                <Container>
                    <h1>Contact</h1>
                    <p>Who runs Doorstep Data, and how to reach us</p>
                </Container>
            </Head>

            <Container>
                <Body>
                    <Cards>
                        <Card>
                            <h3>General</h3>
                            <p>
                                <a href="mailto:info@property-people.co.uk">
                                    info@property-people.co.uk
                                </a>
                            </p>
                        </Card>
                        <Card>
                            <h3>Privacy and data</h3>
                            <p>
                                <a href="mailto:asad@property-people.co.uk">
                                    asad@property-people.co.uk
                                </a>
                            </p>
                        </Card>
                        <Card>
                            <h3>Something looks wrong</h3>
                            <p>
                                <a href="mailto:info@property-people.co.uk?subject=Doorstep%20Data%20-%20incorrect%20figure">
                                    Tell us
                                </a>{" "}
                                &mdash; include the postcode.
                            </p>
                        </Card>
                    </Cards>

                    <h2>Who we are</h2>
                    <p>
                        Doorstep Data is a trading name of{" "}
                        <strong>Property People London Ltd</strong>, an estate and letting agency in
                        south west London, trading since 2010. The site exists because we were
                        already pulling this information together for our own clients and there was
                        no reason to keep it to ourselves.
                    </p>
                    <p>
                        It is free, it needs no account, and it is not a lead-generation form in
                        disguise. We do not ask for your email and we will not contact you unless
                        you contact us first &mdash; see the{" "}
                        <Link to="/privacy-policy">privacy policy</Link>, which is short because
                        there is genuinely not much to say.
                    </p>

                    <h2>If a figure looks wrong</h2>
                    <p>
                        Tell us and we will look. Every number in a report comes from a named public
                        register with its release date shown on the card, so the usual answer is
                        that the register itself is out of date or that an address is recorded
                        differently there than you would write it. Either way it is worth knowing,
                        and sometimes it is our bug.
                    </p>
                    <p>
                        We cannot change what a register says. Corrections to council tax bands go
                        to the Valuation Office Agency, EPCs to the assessor who lodged the
                        certificate, and sold prices to HM Land Registry.
                    </p>

                    <h2>Company details</h2>
                    <Details>
                        <dt>Legal entity</dt>
                        <dd>Property People London Ltd</dd>
                        <dt>Registered in</dt>
                        <dd>England and Wales, company number 07355433</dd>
                        <dt>Registered office</dt>
                        <dd>204 Tooting High Street, London, SW17 0SG</dd>
                        <dt>Data protection</dt>
                        <dd>
                            Registered with the Information Commissioner&rsquo;s Office, reference
                            ZA005338
                        </dd>
                    </Details>
                </Body>
            </Container>
            <SiteFooter />
        </>
    );
}
